import React, { useState, useEffect } from 'react';

// Material UI
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

// Material UI Icons
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import SaveIcon from '@mui/icons-material/Save';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';


// Componentes 


// Funciones para obtener datos
import ObtenerBanner from '../functions/ObtenerBanner';
import InsertarBanner from '../functions/InsertarBanner';
import GuardarBanner from '../functions/GuardarBanner';
import buildImageSrc from '../functions/buildImageSrc';

// Tipos de datos
import BannersEditar from '../types/BannersEditar';

// Indicador de procesamiento
import CircularProgress from '@mui/material/CircularProgress';

interface EditarBannerProps {
    idBanner: number;
    guardarCambios: (banner: BannersEditar) => void;
    cancelarCambios: () => void;
    eliminando: boolean;
}
  
export const EditarBanner: React.FC<EditarBannerProps> = ({ idBanner, guardarCambios, cancelarCambios, eliminando = false }) => {
  
    const [isLoading, setIsLoading] = useState(true);
    const [Banner, setBanner] = useState<BannersEditar | null>(null);
    
    const [errorBanner, setErrorBanner] = useState<string | null>(null);

    const [localId, setLocalId] = useState(0);
    const [localTitulo, setLocalTitulo] = useState('');
    const [localEnlace, setLocalEnlace] = useState('');
    const [localHabilitado, setLocalHabilitado] = useState('');
    const [localMostrarWeb, setLocalMostrarWeb] = useState('');
    const [localFchCrea, setLocalFchCrea] = useState('');
    const [localFchModi, setLocalFchModi] = useState('');
    const [localDadoBaja, setLocalDadoBaja] = useState('');
    const [localFchBaja, setLocalFchBaja] = useState('');
    const [localBannerMime, setLocalBannerMime] = useState<string | null>(null);
    const [localBannerContent, setLocalBannerContent] = useState<string | null>(null);
    const [localBannerName, setLocalBannerName] = useState<string | null>(null);

    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [imageFile, setImageFile] = useState<File | null>(null);

    const fileToBase64 = (file: File): Promise<{base64: string; mime: string; name: string}> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);

            reader.onload = () => {
                const result = reader.result as string;
                const [meta, base64] = result.split(',');

                // meta = "data:image/png;base64"
                const mime = meta.split(':')[1].split(';')[0];

                resolve({ base64, mime, name: file.name });
            }

            reader.onerror = reject;
        });
    };

    useEffect(() => {
        
        const fetchBanner = async () => {
            try {

                const data = await ObtenerBanner(idBanner);

                if (data.banners_ok === 'si') {

                    // Todo ok, banner recuperado

                    const BannerRecuperado: BannersEditar = {
                        id: data.banners[0].id,
                        titulo: data.banners[0].titulo,
                        enlace: data.banners[0].enlace,
                        habilitado: data.banners[0].habilitado,
                        mostrar_web: data.banners[0].mostrar_web,
                        fchalta: data.banners[0].fchalta,
                        fchmodificado: data.banners[0].fchmodificado,
                        dado_baja: data.banners[0].dado_baja,
                        fchbaja: data.banners[0].fchbaja,
                        banner_mime: data.banners[0].banner_mime,
                        banner_content: data.banners[0].banner_content,
                        banner_name: data.banners[0].banner_name,
                        imageSrc: buildImageSrc(data.banners[0].banner_mime, data.banners[0].banner_content) || null

                    };

                    setImagePreview(BannerRecuperado.imageSrc ?? null);

                    setBanner(BannerRecuperado);

                    setLocalId(data.banners[0].id);
                    setLocalTitulo(data.banners[0].titulo);
                    setLocalEnlace(data.banners[0].enlace);
                    setLocalHabilitado(data.banners[0].habilitado);
                    setLocalMostrarWeb(data.banners[0].mostrar_web);
                    setLocalFchCrea(data.banners[0].fchalta);
                    setLocalFchModi(data.banners[0].fchmodificado);
                    setLocalDadoBaja(data.banners[0].dado_baja);
                    setLocalFchBaja(data.banners[0].fchbaja);
                    setLocalBannerMime(data.banners[0].banner_mime);
                    setLocalBannerContent(data.banners[0].banner_content);
                    setLocalBannerName(data.banners[0].banner_name);

                    setErrorBanner(null);
                } else {
                    
                    // Hubo errores al recuperar el Banner
                    setErrorBanner(data.mensaje);
                    setLocalId(0);
                    setBanner(null);
                    setLocalTitulo('');
                    setLocalEnlace('');
                    setLocalHabilitado('');
                    setLocalMostrarWeb('');
                    setLocalFchCrea('');
                    setLocalFchModi('');
                    setLocalDadoBaja('');
                    setLocalFchBaja('');
                    setLocalBannerMime(null);
                    setLocalBannerContent(null);
                    setLocalBannerName(null);
                }

                setIsLoading(false);
                                
            } catch (err) {
                setErrorBanner('Error al obtener la información del Banner.');
                setLocalId(0);
                setBanner(null);
                setLocalTitulo('');
                setLocalEnlace('');
                setLocalHabilitado('');
                setLocalMostrarWeb('');
                setLocalFchCrea('');
                setLocalFchModi('');
                setLocalDadoBaja('');
                setLocalFchBaja('');
                setLocalBannerMime(null);
                setLocalBannerContent(null);
                setLocalBannerName(null);

                setIsLoading(false);
            }
        };

        if (idBanner > 0) {
            // Estoy editando un Banner existente
            fetchBanner();
        } else {
            // Estoy creando un nuevo Banner
            setIsLoading(false);
            setBanner({id:0, titulo:'', enlace:'', habilitado:'', mostrar_web: '', fchalta:'', fchmodificado:'', dado_baja:'', fchbaja:'', banner_mime:null, banner_content:null, banner_name:null, imageSrc: null});
            setImagePreview(null);
            setLocalId(0);
            setLocalTitulo('');
            setLocalEnlace('');
            setLocalHabilitado('');
            setLocalMostrarWeb('');
            setLocalFchCrea('');
            setLocalFchModi('');
            setLocalFchBaja('');
            setLocalHabilitado('S');
            setLocalDadoBaja('N');
            setLocalBannerMime(null);
            setLocalBannerContent(null);
            setLocalBannerName(null);
        }

    }, []);

    const handleChangeLocalTitulo = (event: React.ChangeEvent<HTMLInputElement>) => {
        // check max 50 characters
        const value = event.target.value;
        if (value.length <= 50) {
            setLocalTitulo(value);
        }
    };

    const handleChangeLocalEnlace = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalEnlace(event.target.value);
    };

    const handleChangeLocalBannerMime = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalBannerMime(event.target.value);
    };

    const handleChangeLocalBannerContent = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalBannerContent(event.target.value);
    };

    const handleChangeLocalBannerName = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalBannerName(event.target.value);
    };

    const handleChangeLocalHabilitado = (event: SelectChangeEvent) => {
        let valor = event.target.value;
        let valorMayuscula = valor.toUpperCase();
        setLocalHabilitado(valorMayuscula);
    };

    const handleChangeLocalMostrarWeb = (event: SelectChangeEvent) => {
        let valor = event.target.value;
        let valorMayuscula = valor.toUpperCase();
        setLocalMostrarWeb(valorMayuscula);
    };

    const handleChangeLocalDadoBaja = (event: SelectChangeEvent) => {
        let valor = event.target.value;
        let valorMayuscula = valor.toUpperCase();
        setLocalDadoBaja(valorMayuscula);
    };

    const handleRemoveImage = () => {
        setImageFile(null);
        setImagePreview(null);
        setLocalBannerMime(null);
        setLocalBannerContent(null);
        setLocalBannerName('');
    };

    const handleChangeImage = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith("image/")) {
            alert("Solo se permiten imágenes");
            return;
        }

        const objectUrl = URL.createObjectURL(file);
        const img = new Image();

        img.onload = () => {
            const height = img.height;
            const width = img.width;

            if (height != 1333 && height != 667) {
                alert(`La altura debe ser 1333px o 667px (actual: ${height}px)`);
                URL.revokeObjectURL(objectUrl);
                return;
            }
            if (width != 2000 && width != 1000) {
                alert(`El ancho debe ser 2000px o 1000px (actual: ${width}px)`);
                URL.revokeObjectURL(objectUrl);
                return;
            }

            setImageFile(file);
            setLocalBannerMime(file.type);
            setLocalBannerName(file.name);

            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result as string);
            };

            reader.readAsDataURL(file);
            
            URL.revokeObjectURL(objectUrl);
        };

        img.onerror = () => {
            alert("Error al cargar la imagen");
            URL.revokeObjectURL(objectUrl);
        };

        img.src = objectUrl; // dispara el onload

    };
    
    const guardarCambiosLocal = async () => {

        let banner_name = localBannerName;
        let banner_mime = localBannerMime;
        let banner_content = localBannerContent;

        if (imageFile) {
            const result = await fileToBase64(imageFile);
            banner_name = result.name;
            banner_mime = result.mime;
            banner_content = result.base64;
        } else {
            setLocalBannerMime(Banner?.banner_mime ?? null);
            setLocalBannerContent(Banner?.banner_content ?? null);
            setLocalBannerName(Banner?.banner_name ?? null);
            banner_name = localBannerName;
            banner_mime = localBannerMime;
            banner_content = localBannerContent;
        }

        if (idBanner === 0) {
            // Crear nuevo Banner

            const fechaActual = new Date().toISOString();
            const guardarFecha = fechaActual.slice(0, 10);

            const BannerNuevo: BannersEditar = {
                id: 0,
                titulo: localTitulo,
                enlace: localEnlace,
                habilitado: localHabilitado,
                mostrar_web: localMostrarWeb,
                fchalta: guardarFecha,
                fchmodificado: guardarFecha,
                dado_baja: 'N',
                fchbaja: '',
                banner_mime: banner_mime,
                banner_content: banner_content,
                banner_name: banner_name
            };
            
            console.log('Datos a insertar:', BannerNuevo);
            try {
                const BannerInsertado = await InsertarBanner(BannerNuevo);

                if (BannerInsertado.banner_insertado === 'si') {
                    guardarCambios(BannerNuevo);
                } else {
                    alert('Error al guardar banner nuevo: ' + BannerInsertado.mensaje);
                }
            } catch (error) {
                alert('Error al guardar banner nuevo: ' + error);
            }
        } else {

            if (eliminando) {
                
                // Se realiza la baja lógica del Banner
                // HABILITADO = N
                // DADO_BAJA = S
                const fechaActual = new Date().toISOString();
                //const guardarFecha = fechaActual.slice(0, 10)+" "+fechaActual.slice(11, 19);
                const guardarFecha = fechaActual.slice(0, 10);

                // Modificar datos Banner
                const BannerEditado: BannersEditar = {
                    id: idBanner,
                    titulo: localTitulo,
                    enlace: localEnlace,
                    habilitado: 'N',
                    mostrar_web: localMostrarWeb,
                    fchalta: localFchCrea,
                    fchmodificado: guardarFecha,
                    dado_baja: 'S',
                    fchbaja: guardarFecha,
                    banner_mime: localBannerMime,
                    banner_content: localBannerContent,
                    banner_name: localBannerName
                };

                const respuesta = await GuardarBanner(BannerEditado);

                if (respuesta.banner_guardado === 'si') {
                    guardarCambios(BannerEditado);
                } else {
                    alert('Eliminar - Error al guardar los cambios: ' + respuesta.mensaje);
                }
                
            } else {
                
                // Modificar Banner
                const fechaActual = new Date().toISOString();
                //const guardarFecha = fechaActual.slice(0, 10)+" "+fechaActual.slice(11, 19);
                const guardarFecha = fechaActual.slice(0, 10);

                // Modificar datos Banner
                const BannerEditado: BannersEditar = {
                    id: idBanner,
                    titulo: localTitulo,
                    enlace: localEnlace,
                    habilitado: localHabilitado,
                    mostrar_web: localMostrarWeb,
                    fchalta: localFchCrea,
                    fchmodificado: guardarFecha,
                    dado_baja: localDadoBaja,
                    fchbaja: localDadoBaja === 'S' ? guardarFecha : '',
                    banner_mime: banner_mime,
                    banner_content: banner_content,
                    banner_name: banner_name
                };

                const respuesta = await GuardarBanner(BannerEditado);

                if (respuesta.banner_guardado === 'si') {
                    guardarCambios(BannerEditado);
                } else {
                    alert('Modificar - Error al guardar los cambios: ' + respuesta.mensaje);
                    console.log('Datos enviados para guardar:', BannerEditado);
                    console.log('Respuesta del servidor:', respuesta);
                }
            }
        }
    };
    return (
        <>
            {isLoading && (
                <div>
                    <p className="text-lg">Por favor espere</p>
                    <CircularProgress />
                </div>
            )}
            {(!isLoading && Banner) && (
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 mt-5">
                    {eliminando ? (
                        <h1 className="font-metropolis text-xl text-red-700 font-bold mb-2">Eliminando Banner: {Banner?.titulo}</h1>
                    ) : (
                        <h1 className="font-metropolis text-xl font-bold mb-2">{idBanner > 0 ? "Editando Banner: " + Banner?.titulo : "Nuevo Banner"}</h1>
                    )}
                    {/* Campos principales */}     
                    <div className="border-t-2 border-b-2 border-vp-azul w-full flex flex-row pt-3 justify-between items-start flex-wrap mb-2 pb-2">
                        <div className="flex flex-col justify-start items-center max-w-[120px] flex-grow w-full">
                            <TextField id="id" label="Id" variant="outlined" value={localId}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                                disabled
                            />
                        </div>

                        {/* Título */}
                        <div className="flex flex-col justify-start items-center flex-grow w-full">
                            <TextField id="titulo" label="Título" variant="outlined" value={localTitulo}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                                onChange={handleChangeLocalTitulo}
                                disabled={eliminando}
                            />
                        </div>

                        {/* Enlace */}
                        <div className="flex flex-col justify-start items-center flex-grow w-full">
                            <TextField id="enlace" label="Enlace" variant="outlined" value={localEnlace}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                                onChange={handleChangeLocalEnlace}
                                disabled={eliminando}
                            />
                        </div>


                        {/* Nombre archivo */}
                        <div className="flex flex-col justify-start items-center max-w-[420px] flex-grow w-full">
                            <TextField id="nombreArchivo" label="Nombre archivo" variant="outlined" value={localBannerName}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                                onChange={handleChangeLocalBannerName}
                                disabled={true}
                            />
                        </div>

                        {/* Fechas */}
                        {Banner.id > 0 && (<>
                            <div className="flex flex-col justify-between items-center max-w-[170px] flex-grow w-full">
                                <TextField id="fchcrea" label="Fec. Creado" variant="outlined" value={localFchCrea}
                                    sx={{
                                        '& .MuiOutlinedInput-input': {
                                            padding: '8px',
                                        },
                                        '& .MuiInputLabel-outlined': {
                                            top: '-6px',
                                        },
                                        '& .MuiInputLabel-shrink': {
                                            top: '0px',
                                        },
                                        width: '100%',
                                        marginRight: '6px',
                                        marginBottom: '8px',
                                    }}
                                    disabled
                                />
                            </div>
                            <div className="flex flex-col justify-between items-center max-w-[170px] flex-grow w-full">
                                <TextField id="fchcmodi" label="Fec. Modificado" variant="outlined" value={localFchModi}
                                    sx={{
                                        '& .MuiOutlinedInput-input': {
                                            padding: '8px',
                                        },
                                        '& .MuiInputLabel-outlined': {
                                            top: '-6px',
                                        },
                                        '& .MuiInputLabel-shrink': {
                                            top: '0px',
                                        },
                                        width: '100%',
                                        marginRight: '6px',
                                        marginBottom: '8px',
                                    }}
                                    disabled
                                />
                            </div>                                    
                        </>)
                        }

                        {/* Habilitado */}
                        <div className="flex flex-col justify-between items-center max-w-[100px] flex-grow w-full">
                            <FormControl fullWidth sx={{marginRight: '6px',}}>
                                <InputLabel id="label-habilitado"
                                    sx={{top: '-6px',
                                        '&.MuiInputLabel-shrink': {
                                            transform: 'translate(14px, 0px) scale(0.75)',
                                        },
                                    }}
                                    >
                                        Habilitado
                                    </InputLabel>
                                <Select
                                    labelId="label-habilitado"
                                    id="habilitado"
                                    value={localHabilitado}
                                    label="Habilitado"
                                    sx={{
                                        '& .MuiOutlinedInput-input': {
                                            padding: '8px',
                                        },
                                        width: '100%',
                                        marginRight: '6px',
                                        marginBottom: '8px',
                                    }}
                                    onChange={handleChangeLocalHabilitado}
                                    disabled={eliminando}
                                    >
                                    <MenuItem value={'S'}>Si</MenuItem>
                                    <MenuItem value={'N'}>No</MenuItem>
                                </Select>
                            </FormControl> 
                        </div>

                        {/* Mostrar_web */}
                        <div className="flex flex-col justify-between items-center max-w-[120px] flex-grow w-full">
                            <FormControl fullWidth sx={{marginRight: '6px',}}>
                                <InputLabel id="label-mostrar-web"
                                    sx={{top: '-6px',
                                        '&.MuiInputLabel-shrink': {
                                            transform: 'translate(14px, 0px) scale(0.75)',
                                        },
                                    }}
                                    >
                                        Mostrar web
                                    </InputLabel>
                                <Select
                                    labelId="label-mostrar-web"
                                    id="mostrar-web"
                                    value={localMostrarWeb}
                                    label="Mostrar web"
                                    sx={{
                                        '& .MuiOutlinedInput-input': {
                                            padding: '8px',
                                        },
                                        width: '100%',
                                        marginRight: '6px',
                                        marginBottom: '8px',
                                    }}
                                    onChange={handleChangeLocalMostrarWeb}
                                    disabled={eliminando}
                                    >
                                    <MenuItem value={'S'}>Si</MenuItem>
                                    <MenuItem value={'N'}>No</MenuItem>
                                </Select>
                            </FormControl> 
                        </div>

                        {/* Dado_baja */}
                        <div className="flex flex-col justify-between items-center max-w-[100px] flex-grow w-full">
                            <FormControl fullWidth sx={{marginRight: '6px',}}>
                                <InputLabel id="label-dadobaja"
                                    sx={{top: '-6px',
                                        '&.MuiInputLabel-shrink': {
                                            transform: 'translate(14px, 0px) scale(0.75)',
                                        },
                                    }}
                                    >
                                        Baja
                                    </InputLabel>
                                <Select
                                    labelId="label-dadobaja"
                                    id="dadobaja"
                                    value={localDadoBaja}
                                    label="Baja"
                                    sx={{
                                        '& .MuiOutlinedInput-input': {
                                            padding: '8px',
                                        },
                                        width: '100%',
                                        marginRight: '6px',
                                        marginBottom: '8px',
                                    }}
                                    onChange={handleChangeLocalDadoBaja}
                                    disabled={eliminando}
                                    >
                                    <MenuItem value={'S'}>Si</MenuItem>
                                    <MenuItem value={'N'}>No</MenuItem>
                                </Select>
                            </FormControl> 
                        </div>

                        {localDadoBaja === 'S' && (
                            <div className="flex flex-col justify-between items-center max-w-[170px] flex-grow w-full">
                                <TextField id="fchbaja" label="Fec. Baja" variant="outlined" value={localFchBaja}
                                    sx={{
                                        '& .MuiOutlinedInput-input': {
                                            padding: '8px',
                                        },
                                        '& .MuiInputLabel-outlined': {
                                            top: '-6px',
                                        },
                                        '& .MuiInputLabel-shrink': {
                                            top: '0px',
                                        },
                                        width: '100%',
                                        marginRight: '6px',
                                        marginBottom: '8px',
                                    }}
                                    disabled
                                />
                            </div>)
                        }

                        {/* Preview y botón para subir imagen nueva */}
                        <div className="flex flex-col justify-start items-center max-w-[220px] flex-grow w-full">

                            {/* Label */}
                            <Typography
                                variant="caption"
                                sx={{
                                    alignSelf: 'flex-start',
                                    marginBottom: '4px',
                                    marginLeft: '4px',
                                    color: 'rgba(0, 0, 0, 0.6)'
                                }}
                            >
                                Banner:<br />(Alto: 1333/667px, Ancho: 2000/1000px)
                            </Typography>

                            {/* Preview */}
                            {imagePreview ? (
                                <img
                                    src={imagePreview}
                                    alt="banner"
                                    className="w-[360px] h-[240px] object-contain border rounded mb-2"
                                />
                            ) : (
                                <div className="w-[360px] h-[240px] flex items-center justify-center border rounded mb-2 text-gray-400">
                                    Sin imagen
                                </div>
                            )}

                            {/* Botón subir */}
                            <Button
                                variant="contained"
                                component="label"
                                disabled={eliminando}
                                size="small"
                                sx={{ mb: 1 }}
                            >
                                Cambiar
                                <input
                                    hidden
                                    type="file"
                                    accept="image/*"
                                    onChange={handleChangeImage}
                                />
                            </Button>
                            <Button
                                variant="contained"
                                color="error"
                                size="small"
                                onClick={handleRemoveImage}
                                disabled={!imagePreview || eliminando}
                            >
                                Quitar
                            </Button>
                        </div>

                    </div>
                    <div className="flex flex-row justify-end items-center space-x-2">
                        {eliminando ? (
                            <>
                                <button className="mt-3 vp-btn-rojo-sm" onClick={guardarCambiosLocal} ><DeleteForeverIcon /> Eliminar</button>
                                <button className="mt-3 vp-btn-azul-sm" onClick={cancelarCambios} ><SettingsBackupRestoreIcon /> Cancelar</button>
                            </>
                        ) : (
                            <>
                                <button className="mt-3 vp-btn-azul-sm" onClick={guardarCambiosLocal} ><SaveIcon /> Guardar Cambios</button>
                                <button className="mt-3 vp-btn-rojo-sm" onClick={cancelarCambios} ><SettingsBackupRestoreIcon /> Cancelar</button>
                            </>
                        )}
                        
                    </div>
                </div>
            )}
        </>
    );
};
